import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  PiSquaresFourLight,
  PiBriefcaseLight,
  PiCalendarCheckLight,
  PiClipboardTextLight,
  PiChartLineUpLight,
  PiCaretDoubleLeftLight,
  PiCaretDoubleRightLight,
  PiSignOutLight,
  PiShieldCheckLight,
  PiGearSixLight,
  PiUserGearLight,
  PiUserLight,
  PiUsersLight,
  PiSignatureLight,
  PiXLight,
  PiBookOpenTextLight
} from 'react-icons/pi';
import { useAuthStore } from '../../store';
import { EMPLOYER_PERMISSIONS, getDefaultAllowedPath, hasPermission, isEmployerSuperAdmin } from '../../utils/rbac';
import logoMark from '../../assets/images/sanspath-logo-mark.png';

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: PiSquaresFourLight, permission: EMPLOYER_PERMISSIONS.DASHBOARD },
  { name: 'Jobs', href: '/jobs', icon: PiBriefcaseLight, permission: EMPLOYER_PERMISSIONS.JOBS },
  { name: 'Candidates', href: '/candidates', icon: PiUsersLight, permission: EMPLOYER_PERMISSIONS.CANDIDATES },
  { name: 'Assessments', href: '/assessments', icon: PiClipboardTextLight, permission: EMPLOYER_PERMISSIONS.ASSESSMENTS },
  { name: 'Interviews', href: '/interviews', icon: PiCalendarCheckLight, permission: EMPLOYER_PERMISSIONS.INTERVIEWS },
  { name: 'Offers', href: '/offers', icon: PiSignatureLight, permission: EMPLOYER_PERMISSIONS.OFFERS },
  { name: 'Reports', href: '/reports', icon: PiChartLineUpLight, permission: EMPLOYER_PERMISSIONS.REPORTS },
  { name: 'Configuration', href: '/config', icon: PiGearSixLight, permission: EMPLOYER_PERMISSIONS.CONFIGURATION },
  { name: 'Roles', href: '/roles', icon: PiShieldCheckLight, permission: EMPLOYER_PERMISSIONS.ROLES, superAdminOnly: true },
  { name: 'Admin RBAC', href: '/users', icon: PiUserGearLight, permission: EMPLOYER_PERMISSIONS.USERS, superAdminOnly: true },
  { name: 'Profile', href: '/profile', icon: PiUserLight, permission: EMPLOYER_PERMISSIONS.PROFILE }
];

const isRouteActive = (pathname, href) => (
  href === '/dashboard' ? pathname === '/dashboard' || pathname === '/' : pathname.startsWith(href)
);

const Sidebar = ({ isOpen, onToggle, onNavigate, isMobile: isMobileProp }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuthStore();
  const isMobile = Boolean(isMobileProp);

  const handleLinkClick = (path) => {
    if (onNavigate) {
      onNavigate(path);
      return;
    }
    navigate(path);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (isMobile) {
    return (
      <>
        <div
          className={`fixed inset-0 z-40 bg-[#070b14]/60 backdrop-blur-sm transition-opacity duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${
            isOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
          }`}
          onClick={onToggle}
        />

        <aside
          className={`fixed inset-y-0 left-0 z-50 flex w-[292px] max-w-[85vw] flex-col border-r border-white/5 bg-gradient-to-b from-[#0f172a] via-[#0b1221] to-[#020617] shadow-2xl transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${
            isOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <SidebarBody
            expanded
            user={user}
            pathname={location.pathname}
            onNavigate={handleLinkClick}
            onLogout={handleLogout}
            onClose={onToggle}
          />
        </aside>
      </>
    );
  }

  return (
    <div className={`relative hidden h-full shrink-0 transition-[width] duration-400 ease-[cubic-bezier(0.16,1,0.3,1)] lg:flex ${isOpen ? 'w-[280px]' : 'w-[88px]'}`}>
      <aside className="absolute inset-0 z-20 flex h-full flex-col overflow-hidden bg-gradient-to-b from-[#0f172a] via-[#0b1221] to-[#020617] border-r border-white/5 shadow-[4px_0_24px_rgba(0,0,0,0.15)]">
        <SidebarBody
          expanded={isOpen}
          isMobile={false}
          user={user}
          pathname={location.pathname}
          onNavigate={handleLinkClick}
          onLogout={handleLogout}
          onToggle={onToggle}
        />
      </aside>
    </div>
  );
};

const SidebarBody = ({ expanded, isMobile = true, user, pathname, onNavigate, onLogout, onClose, onToggle }) => {
  const initials = user?.name
    ? user.name.charAt(0).toUpperCase()
    : `${user?.firstName?.charAt(0) || ''}${user?.lastName?.charAt(0) || ''}`.trim() || 'S';
  
  const regularNavigation = navigation.filter((item) => (
    !item.superAdminOnly && hasPermission(user, item.permission)
  ));
  
  const adminNavigation = isEmployerSuperAdmin(user)
    ? navigation.filter((item) => item.superAdminOnly)
    : [];
    
  const homePath = hasPermission(user, EMPLOYER_PERMISSIONS.DASHBOARD)
    ? '/dashboard'
    : getDefaultAllowedPath(user);
  const guideActive = isRouteActive(pathname, '/user-guide');

  return (
    <div className={`relative flex h-full w-full flex-col overflow-hidden ${expanded ? 'px-5 py-6' : 'items-center px-3 py-6'}`}>
      {/* Background glow effects for premium dark aesthetic */}
      <div className="pointer-events-none absolute -left-10 -top-10 z-0 h-96 w-96 rounded-full bg-sky-800/40 blur-[90px] animate-blob" />
      <div className="pointer-events-none absolute top-1/3 -right-20 z-0 h-80 w-80 rounded-full bg-indigo-800/30 blur-[90px] animate-blob" style={{ animationDelay: '3s' }} />
      <div className="pointer-events-none absolute -bottom-10 -left-10 z-0 h-96 w-96 rounded-full bg-cyan-800/30 blur-[90px] animate-blob" style={{ animationDelay: '6s' }} />

      <div className={`relative z-10 mb-8 flex w-full items-center ${expanded ? 'justify-between' : 'flex-col justify-center gap-4'}`}>
        <button
          onClick={() => onNavigate(homePath)}
          className={`group flex items-center ${expanded ? 'gap-3.5' : 'justify-center'} transition-transform duration-300 hover:scale-[1.02] active:scale-95`}
          aria-label="Go to dashboard"
        >
          <img
            src={logoMark}
            alt="Sanspath"
            className={`${expanded ? 'h-[60px] w-[60px]' : 'h-[60px] w-[60px]'} shrink-0 object-contain drop-shadow-lg transition-transform duration-300 group-hover:scale-105`}
            style={{ filter: 'brightness(0) invert(1)' }} 
          />
          {expanded && (
            <span className="min-w-0 flex flex-col items-start">
              <span className="font-display text-[20px] font-bold tracking-tight text-white leading-none">
                Sanspath
              </span>
              <span className="mt-1 text-[10px] font-bold uppercase tracking-[0.2em] text-sky-400">
                Employer
              </span>
            </span>
          )}
        </button>

        {expanded && (
          <button
            onClick={isMobile ? onClose : onToggle}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition-all duration-200 hover:bg-white/10 hover:text-white active:scale-95"
            aria-label={isMobile ? 'Close navigation' : 'Collapse navigation'}
          >
            {isMobile ? <PiXLight size={18} /> : <PiCaretDoubleLeftLight size={18} />}
          </button>
        )}

        {!expanded && !isMobile && (
          <button
            onClick={onToggle}
            className="flex h-10 w-10 items-center justify-center rounded-xl text-slate-400 bg-white/5 border border-white/5 transition-all duration-200 hover:bg-sky-500/20 hover:text-sky-400 hover:border-sky-500/30 active:scale-95 shadow-sm"
            aria-label="Expand navigation"
            title="Expand navigation"
          >
            <PiCaretDoubleRightLight size={18} />
          </button>
        )}
      </div>

      <nav className={`relative z-10 flex-1 overflow-y-auto custom-scrollbar ${expanded ? '-mr-5 w-[calc(100%+1.25rem)] space-y-1.5 pr-5' : '-mr-3 flex w-[calc(100%+0.75rem)] flex-col items-center gap-2 pr-3'}`}>
        {/* ── Regular navigation ── */}
        {regularNavigation.map((item) => {
          const active = isRouteActive(pathname, item.href);
          const Icon = item.icon;

          return (
            <button
              key={item.name}
              onClick={() => onNavigate(item.href)}
              className={`
                group relative flex items-center rounded-xl transition-all duration-300 overflow-hidden
                ${expanded ? 'h-11 w-full gap-3.5 px-3.5 text-left' : 'h-11 w-11 justify-center'}
                ${active
                  ? 'bg-gradient-to-r from-sky-500/10 to-transparent text-sky-400'
                  : 'text-slate-400 hover:bg-white/5 hover:text-slate-200'
                }
              `}
              title={expanded ? undefined : item.name}
              aria-current={active ? 'page' : undefined}
            >
              
              <Icon 
                size={expanded ? 18 : 20} 
                className={`relative z-10 transition-transform duration-300 ${active ? 'scale-110 drop-shadow-sm text-sky-400' : 'group-hover:scale-110 text-slate-400 group-hover:text-slate-200'}`} 
              />
              
              {expanded && (
                <span className={`relative z-10 text-[14px] tracking-wide ${active ? 'font-semibold' : 'font-medium'}`}>
                  {item.name}
                </span>
              )}
              
              {!expanded && (
                <span className="pointer-events-none absolute left-[4.5rem] z-50 whitespace-nowrap rounded-lg border border-slate-700 bg-slate-800 px-3.5 py-2 text-xs font-semibold text-white opacity-0 shadow-[0_10px_25px_rgba(0,0,0,0.5)] transition-all duration-200 group-hover:translate-x-1 group-hover:opacity-100">
                  {item.name}
                </span>
              )}
            </button>
          );
        })}

        {/* ── Admin section ── */}
        {adminNavigation.length > 0 && (
          <>
            <div className={`${expanded ? 'mx-2 my-4' : 'my-4'}`}>
              <div className="h-px w-full bg-gradient-to-r from-transparent via-slate-700 to-transparent" />
              {expanded && (
                <p className="mt-4 mb-2 px-2 text-[10.5px] font-bold uppercase tracking-[0.25em] text-slate-500">
                  Admin
                </p>
              )}
              {!expanded && (
                <div className="mt-3 flex justify-center">
                  <div className="h-1 w-1 rounded-full bg-slate-600 shadow-[0_0_5px_rgba(255,255,255,0.2)]" />
                </div>
              )}
            </div>

            {adminNavigation.map((item) => {
              const active = isRouteActive(pathname, item.href);
              const Icon = item.icon;

              return (
                <button
                  key={item.name}
                  onClick={() => onNavigate(item.href)}
                  className={`
                    group relative flex items-center rounded-xl transition-all duration-300 overflow-hidden
                    ${expanded ? 'h-11 w-full gap-3.5 px-3.5 text-left' : 'h-11 w-11 justify-center'}
                    ${active
                      ? 'bg-gradient-to-r from-indigo-500/10 to-transparent text-indigo-400'
                      : 'text-slate-400 hover:bg-white/5 hover:text-slate-200'
                    }
                  `}
                  title={expanded ? undefined : item.name}
                  aria-current={active ? 'page' : undefined}
                >
                  
                  <Icon 
                    size={expanded ? 18 : 20} 
                    className={`relative z-10 transition-transform duration-300 ${active ? 'scale-110 drop-shadow-sm text-indigo-400' : 'group-hover:scale-110 text-slate-400 group-hover:text-slate-200'}`} 
                  />
                  
                  {expanded && (
                    <span className={`relative z-10 text-[14px] tracking-wide ${active ? 'font-semibold' : 'font-medium'}`}>
                      {item.name}
                    </span>
                  )}
                  
                  {!expanded && (
                    <span className="pointer-events-none absolute left-[4.5rem] z-50 whitespace-nowrap rounded-lg border border-slate-700 bg-slate-800 px-3.5 py-2 text-xs font-semibold text-white opacity-0 shadow-[0_10px_25px_rgba(0,0,0,0.5)] transition-all duration-200 group-hover:translate-x-1 group-hover:opacity-100">
                      {item.name}
                    </span>
                  )}
                </button>
              );
            })}
          </>
        )}
      </nav>

      <div className={`mt-auto w-full pt-6 ${expanded ? 'space-y-3' : 'flex flex-col items-center gap-4'}`}>
        <button
          onClick={() => onNavigate('/user-guide')}
          className={`
            group relative flex shrink-0 items-center overflow-hidden border transition-all
            ${expanded ? 'h-11 w-full justify-start gap-3 rounded-xl px-3.5 text-sm font-medium tracking-wide' : 'h-[42px] w-[42px] justify-center rounded-full'}
            ${guideActive
              ? 'border-sky-500/40 bg-sky-500/15 text-sky-300'
              : 'border-slate-700/50 bg-slate-800/60 text-slate-400 hover:border-sky-500/30 hover:bg-sky-500/10 hover:text-sky-300'
            }
          `}
          title="Guide"
          aria-label="Open employer guide"
          aria-current={guideActive ? 'page' : undefined}
        >
          <PiBookOpenTextLight size={18} className="transition-transform duration-300 group-hover:scale-110" />
          {expanded && <span>Guide</span>}
        </button>

        <div className={`flex w-full ${expanded ? 'items-center justify-between' : 'flex-col items-center gap-4'}`}>
          <button
            onClick={() => onNavigate('/profile')}
            className={`group flex items-center ${expanded ? 'min-w-0 flex-1 pr-2 text-left' : 'justify-center'} rounded-xl outline-none`}
            title={expanded ? undefined : 'Profile'}
          >
            <div className="flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-full bg-slate-800/80 text-[13px] font-semibold text-slate-200 border border-slate-700/50 group-hover:bg-slate-700 group-hover:text-white group-hover:border-slate-600 transition-all">
              {initials}
            </div>
            {expanded && (
              <div className="ml-3 min-w-0 flex-1">
                <p className="truncate text-[14px] font-medium text-slate-300 group-hover:text-white transition-colors">
                  {user?.name || user?.companyName || 'Employer'}
                </p>
                <p className="truncate text-[12px] text-slate-500 group-hover:text-slate-400 transition-colors">
                  {user?.email || 'Account settings'}
                </p>
              </div>
            )}
          </button>

          <button
            onClick={onLogout}
            className="flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-full text-slate-400 hover:bg-rose-500/10 hover:text-rose-400 transition-colors"
            title="Sign Out"
          >
            <PiSignOutLight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
